import moment from 'moment';
import isDate from 'lodash/isDate';
import { translate } from './translateHelper';

import { customformat } from './dateFormats';


export const compareDatesToDayPrecision = (d1, d2) => {
    return d1 && d2 &&
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
};

const formateTimezoneOffset = (offsetInMinutes, sign) => {
    const absOffsetValue = Math.abs(offsetInMinutes);
    return `${sign}${Math.trunc(absOffsetValue / 60).toString().padStart(2, '0')}:${Math.trunc(absOffsetValue % 60).toString().padStart(2, '0')}`;
};

const getFormatedTimezoneOffset = (offsetInMinutes) => {
    const sign = offsetInMinutes <= 0 ? '+' : '-';
    return formateTimezoneOffset(offsetInMinutes, sign);
};

const getDateObject = (date) => {

    if (typeof date === 'number') {
        return new Date(date);
    }
    else if (typeof date === 'string') {
        const isoDate = new Date(date); // parsing dates in JS is unreliable, it will parse also the string 'AAA-0002' sucessfully
        if (isDate(isoDate)) {
            return isoDate;
        }
    }
    else {
        return date;
    }
};

export const toLocalDateTimeOffsetString = (date, { includeMilliseconds = true } = {}) => {

    const dateObj = getDateObject(date);

    const year = `${dateObj.getFullYear()}`.padStart(4, '0');
    const month = `${dateObj.getMonth() + 1}`.padStart(2, '0');
    const day = `${dateObj.getDate()}`.padStart(2, '0');
    const hours = `${dateObj.getHours()}`.padStart(2, '0');
    const minutes = `${dateObj.getMinutes()}`.padStart(2, '0');
    const seconds = `${dateObj.getSeconds()}`.padStart(2, '0');
    const milliseconds = `${dateObj.getMilliseconds()}`.padStart(3, '0');

    const msPart = includeMilliseconds ? `.${milliseconds}` : '';
    const offset = getFormatedTimezoneOffset(dateObj.getTimezoneOffset());

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${msPart}${offset}`;
};


export const toLocaleDateTimeString = (date, {
    asUTC = false,
    showMs = false,
    showOffset = false,
    showTimePortion = true,
    showMonthAndYearOnly = false,
    isForEventLog = false
} = {}) => {

    if (!date || date === '') {
        return '-';
    }
    if (isForEventLog) {
        const convertedUTCDate = moment.utc(date);
        date = convertedUTCDate.toISOString();
    }
    let dateObj = getDateObject(date);

    if (isDate(dateObj)) {
        let offsetInMinutes = dateObj.getTimezoneOffset();
        if (asUTC) {
            dateObj = new Date(dateObj.getTime() + offsetInMinutes * 60 * 1000);
            offsetInMinutes = 0;
        }

        let offsetString = '';
        if (showOffset) {
            offsetString = `(${getFormatedTimezoneOffset(offsetInMinutes)})`;
        }

        const h = `${dateObj.getHours()}`.padStart(2, '0');
        const m = `${dateObj.getMinutes()}`.padStart(2, '0');
        const s = `${dateObj.getSeconds()}`.padStart(2, '0');
        const ms = showMs ? '.' + `${dateObj.getMilliseconds()}`.padStart(3, '0') : '';

        let formattedDate = `${dateObj.toLocaleDateString()}`;

        if (showMonthAndYearOnly) {
            formattedDate = `${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
        }

        if (showTimePortion) {
            formattedDate = `${dateObj.toLocaleDateString()} ${h}:${m}:${s}${ms}${offsetString}`;//  hardcoded time format
        }

        return formattedDate;
    }
};

export const convertUtcToLocalDateTime = (dateTime, withoutHours = false, asUTC, format) => {
    const formatPattern = format ? format : `${customformat(navigator.language)} ${withoutHours ? '' : 'HH:mm:ss'}`;
    const localDate = asUTC ? moment.utc(dateTime) : moment.utc(dateTime).local();
    return localDate.format(formatPattern);
};

export const getPriorDateMinusDays = (minusDays) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - minusDays);
    return currentDate;
};

export const getUTCToLocalTimeZone = (date, withoutHours = false, asUTC) => {
    const formatPattern = `${customformat(navigator.language)} ${withoutHours ? '' : 'HH:mm:ss'}`;
    const localDate = asUTC ? moment.utc(date) : moment.utc(date).local();
    return localDate.format(formatPattern);
};

export const formatUtcTimestampDisplay = (timestamp) => {
    if (!timestamp) { return '-'; }
    // UTC time Reformat "YYYY-MM-DDTHH:mm:ss.sssssss" to "MM/DD/YYYYTHH:mm:ss.sssssss"
    const tIndex = timestamp.indexOf('T');
    if (tIndex === -1) { return timestamp; }
    const datePart = timestamp.substring(0, tIndex);
    const timePart = timestamp.substring(tIndex);
    const [yyyy, mm, dd] = datePart.split('-');
    if (!yyyy || !mm || !dd) { return timestamp; }
    return `${mm}/${dd}/${yyyy}${timePart}`;
};

export const getUTCDateString = (date, includeMilliseconds = false) => {
    const utcFormattedDateString = date.toISOString();
    return includeMilliseconds ? utcFormattedDateString : utcFormattedDateString.split('.')[0] + 'Z';
};

export const isUtcTimestamp = (timestamp) => {
    if (typeof timestamp !== 'string') { return false; }
    return timestamp.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(timestamp);
};

export function getDateRangeString(from, to) {
    let retValue = '';
    if (from && to) {
        const formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
        retValue = new Date(from).toLocaleDateString('en-US', formatOptions) + ' - ' + new Date(to).toLocaleDateString('en-US', formatOptions);
    }
    return retValue;
}

export const formatStartDate = (fromDate) => {
    fromDate.setHours(0);
    fromDate.setMinutes(0);
    fromDate.setSeconds(0);
    fromDate.setMilliseconds(0);
    return fromDate;
};


export const formatEndDate = (toDate) => {
    toDate.setHours(23);
    toDate.setMinutes(59);
    toDate.setSeconds(59);
    toDate.setMilliseconds(999);
    return toDate;
};

const buildValueObject = (yDiff, mDiff, dDiff, hourDiff, minDiff, secDiff, firstDateWasLater) => {
    return {
        'years': yDiff,
        'months': mDiff,
        'days': dDiff,
        'hours': hourDiff,
        'minutes': minDiff,
        'seconds': secDiff,
        'firstDateWasLater': firstDateWasLater
    };
};

const preciseDiff = (d1, d2, returnValueObject) => {
    let m1 = moment(d1), m2 = moment(d2), firstDateWasLater;

    m1.add(m2.utcOffset() - m1.utcOffset(), 'minutes'); // shift timezone of m1 to m2

    if (m1.isSame(m2)) {
        return returnValueObject
            ? buildValueObject(0, 0, 0, 0, 0, 0, false)
            : 0;
    }
    if (m1.isAfter(m2)) {
        [m1, m2] = [m2, m1];
        firstDateWasLater = true;
    } else {
        firstDateWasLater = false;
    }

    let yDiff = m2.year() - m1.year();
    let mDiff = m2.month() - m1.month();
    let dDiff = m2.date() - m1.date();
    let hourDiff = m2.hour() - m1.hour();
    let minDiff = m2.minute() - m1.minute();
    let secDiff = m2.second() - m1.second();

    // Helper to handle negative differences and carry over
    const carryOver = (diff, base, nextDiffSetter) => {
        if (diff < 0) {
            nextDiffSetter();
            return base + diff;
        }
        return diff;
    };

    secDiff = carryOver(secDiff, 60, () => minDiff--);
    minDiff = carryOver(minDiff, 60, () => hourDiff--);
    hourDiff = carryOver(hourDiff, 24, () => dDiff--);
    if (dDiff < 0) {
        const daysInLastFullMonth = moment(m2.year() + '-' + (m2.month() + 1), 'YYYY-MM').subtract(1, 'M').daysInMonth();
        if (daysInLastFullMonth < m1.date()) {
            dDiff = daysInLastFullMonth + dDiff + (m1.date() - daysInLastFullMonth);
        } else {
            dDiff = daysInLastFullMonth + dDiff;
        }
        mDiff--;
    }

    if (mDiff < 0) {
        mDiff = 12 + mDiff;
        yDiff--;
    }

    if (returnValueObject) {
        return buildValueObject(yDiff, mDiff, dDiff, hourDiff, minDiff, secDiff, firstDateWasLater);
    }
    return `${yDiff} ${mDiff} ${dDiff} ${hourDiff} ${minDiff} ${secDiff}`;
};

const getDateTimeDifference = (lastSyncDate) => {
    let lastSync = moment.utc(lastSyncDate);
    lastSync = lastSync.local();
    const diff = preciseDiff(lastSync, moment(), true);
    return diff;
};

const getTimeString = (value, labelKey, nextValue, nextLabelKey) => {
    if (!value) { return ''; }
    const next = nextValue ? ` ${nextValue} ${translate(nextLabelKey)}` : '';
    return `${value} ${translate(labelKey)}${next}`;
};

const getSecondsString = (seconds) => {
    if (seconds >= 10 && seconds <= 60) {
        return translate('ABB.Powertrain.Frontend.underOneMinute');
    }
    if (seconds && seconds < 10) {
        return translate('ABB.Powertrain.Frontend.justNow');
    }
    return '-';
};

export const getTimeSinceDateString = (date) => {
    if (!date) { return '-'; }

    const lastSyncDate = getDateTimeDifference(date);
    const {
        seconds = 0,
        minutes = 0,
        hours = 0,
        days = 0,
        months = 0,
        years = 0
    } = lastSyncDate;

    if (years) {
        return getTimeString(years, 'ABB.Powertrain.Frontend.labelYears', months, 'ABB.Powertrain.Frontend.labelMonths');
    }
    if (months) {
        return getTimeString(months, 'ABB.Powertrain.Frontend.labelMonths', days, 'ABB.Powertrain.Frontend.labelDays');
    }
    if (days) {
        return getTimeString(days, 'ABB.Powertrain.Frontend.labelDays', hours, 'ABB.Powertrain.Frontend.labelHours');
    }
    if (hours) {
        return getTimeString(hours, 'ABB.Powertrain.Frontend.labelHours', minutes, 'ABB.Powertrain.Frontend.labelMinutes');
    }
    if (minutes && minutes < 60) {
        return `${minutes} ${translate('ABB.Powertrain.Frontend.labelMinutes')}`;
    }
    return getSecondsString(seconds);

};

export const getNrOfDaysBetween = (from, to) => {
    let difference = 0;

    if (from && to) {
        const fromDate = moment(from);
        const toDate = moment(to);
        if (fromDate.isValid() && toDate.isValid()) {
            difference = toDate.diff(fromDate, 'days') + 1;
        }
    }

    return difference;
};
